import type { Assignment, Run } from "../types";

/**
 * Pure projection: filter a {@link Run}'s assignment list down to the roster
 * for a single section, identified by `(any accepted normalized course id,
 * sectionLabel ?? "")`. A section can serve several course codes (a multi-value
 * `Course ID:` line → one shared section); an assignment carries the student's
 * REQUESTED code, so the roster matches on membership in the section's accepted
 * set rather than a single code.
 *
 * Returns `[]` when `run` is null / undefined (caller may have a not-yet-loaded
 * query) or when nothing matches. Sorts deterministically by
 * `studentIdentifier` (ordinal `localeCompare`) so the same roster always
 * renders in the same order.
 */
export function computeSectionRoster(
  run: Run | null | undefined,
  normalizedCourseIds: readonly string[],
  sectionLabel: string | null,
): Assignment[] {
  if (!run) return [];
  const targetLabel = sectionLabel ?? "";
  const accepted = new Set(normalizedCourseIds);
  const matches = run.assignments.filter(
    (a) =>
      accepted.has(a.normalizedCourseId) &&
      (a.sectionLabel ?? "") === targetLabel,
  );
  return [...matches].sort((a, b) =>
    a.studentIdentifier.localeCompare(b.studentIdentifier),
  );
}
